import React, { useState, useEffect } from 'react';
import { useParams } from "react-router-dom";
import { CircularProgress, TextField, FormGroup, Button, List, ListItem, Typography, makeStyles, Container,
    Select, MenuItem } from '@material-ui/core';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ClearIcon from '@material-ui/icons/Clear';
import UndoIcon from '@material-ui/icons/Undo';
import { Alert, AlertTitle } from '@material-ui/lab';
import { getPlayerBios, addSquad as addTeam, removeFromSquad, getLeague } from '../../data';

export function AddTeam(props) {
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [addSquadEnabled, setAddSquadEnabled] = useState(false);
    const [removePlayersList, setRemovePlayersList] = useState([]);
    const [squadNameError, setSquadNameError] = useState(false);
    const [players, setPlayers] = useState([]);
    const [validatedPlayers, setValidatedPlayers] = useState([]);
    const [inValidatedPlayers, setInvalidatedPlayers] = useState([]);
    const [selectedSquadName, setSelectedSquadName] = useState(undefined);
    const [selectedSquad, setSelectedSquad] = useState([]);
    const [league, setLeague] = useState(props.league);

    useEffect(() => {
        const loadPlayers = async function () {
            const players = await getPlayerBios(league.tournament);
            setPlayers(() => players);
        };
        loadPlayers();
    }, [league]);

    const searchForPlayer = (name) => {
        const searchResults = [];
        for (var i = 0; i < players.length; i++) {
            const player = players[i];
            if (player.name.toLocaleLowerCase().match(name.toLocaleLowerCase()) !== null) {
                searchResults.push(player);
            }
        }
        if (searchResults.length > 1) {
            const exactMatches = searchResults.filter(s => s.name === name);
            if (exactMatches.length === 1) {
                return exactMatches;
            }
        }
        return searchResults;
    };

    const validatePlayerName = () => {
        const playerListText = document.getElementById('players').value;
        const squadPlayers = playerListText.split('\n');
        const validatedPlayers = [];
        const inValidatedPlayers = [];
        squadPlayers.forEach(name => {
            const results = searchForPlayer(name);
            if (results.length === 1) {
                validatedPlayers.push(results[0]);
            } else {
                inValidatedPlayers.push(name);
            }
        });
        setValidatedPlayers(() => [...validatedPlayers]);
        setInvalidatedPlayers(() => [...inValidatedPlayers]);
        if (inValidatedPlayers.length === 0 && validatedPlayers.length > 0) {
            setAddSquadEnabled(true);
        } else {
            setAddSquadEnabled(false);
        }
    };

    const removeFromSquadHandler = async () => {
        const squadName = selectedSquadName;
        const data = {
            'leagueID': league.leagueID,
            'squadName': squadName,
            'players': removePlayersList
        };
        setLoading(true);
        try {
            const response = await removeFromSquad(data);
            const newLeague = await getLeague(league.leagueID);
            setError(undefined);
            setLeague(newLeague);
            changeSquadDisplayed(squadName, newLeague);
            setRemovePlayersList([]);
        } catch (err) {
            setError(`Failed to remove players from - ${err.message}`);
        }
        setLoading(false);
    };

    const addToRemoveList = (player) => {
        setRemovePlayersList(removePlayersList.concat(player.id))
    };

    const removeFromRemoveList = (player) => {
        setRemovePlayersList(removePlayersList.filter(p => p !== player.id))
    };

    const addSquad = async () => {
        const squadName = selectedSquadName;
        if (squadName.length === 0) {
            setSquadNameError(true);
            return;
        } else {
            setSquadNameError(false);
        }
        const data = {
            'tournament': league.tournament,
            'leagueID': league.leagueID,
            'squadName': squadName,
            'players': validatedPlayers.map(p => p.id),
        };
        setLoading(true);
        try {
            const response = await addTeam(data);
            const newLeague = await getLeague(league.leagueID);
            setLeague(newLeague);
            changeSquadDisplayed(squadName, newLeague);
            setError(undefined);
        } catch (err) {
            setError(`Failed to add team - ${err.message}`);
        }
        setLoading(false);
        setAddSquadEnabled(false);
    };

    const useStyles = makeStyles((theme) => ({
        root: {
            display: 'flex',
        },
        table: {
            alignSelf: 'center',
            flex: 'auto',
        },
        button: {
            marginTop: theme.spacing(4),
            marginRight: theme.spacing(2),
        },
        playerList: {
            marginLeft: theme.spacing(2),
            padding: theme.spacing(1),
        },
        dropDown: {
            minWidth: 200,
        }
    }));

    const displayListOfPlayers = (header, playerList) => {
        if (playerList.length === 0) {
            return (<div></div>);
        }
        return (
            <div className={classes.playerList}>
                <Typography variant="h6">
                    {header}
                </Typography>
                <List>
                    {playerList.map(name => {
                        return (
                            <ListItem key={name}>
                                {name}
                            </ListItem>
                        )
                    })}
                </List>
            </div>
        );
    };

    const displayCurrentSquad = (playerList) => {
        if (playerList.length === 0) {
            return (<div></div>);
        }
        const iconStyle = {marginLeft: 'auto', padding: '3px', cursor: 'pointer'};
        return (
            <div className={classes.playerList}>
                <List>
                    {playerList.map(player => {
                        return (
                            <ListItem key={player.id}>
                                <span>
                                {player.name}
                                </span>
                                {removePlayersList.includes(player.id) ?
                                <UndoIcon style={iconStyle} onClick={(e) => removeFromRemoveList(player)}/>
                                : <ClearIcon style={iconStyle} onClick={(e) => addToRemoveList(player)}/>}
                            </ListItem>
                        )
                    })}
                </List>
            </div>
        );
    };

    const onSquadChange = async (e) => {
        changeSquadDisplayed(e.target.value);
    };

    const changeSquadDisplayed = (newSquadName, newLeague) => {
        setSelectedSquadName(newSquadName);
        const newSquad = ((newLeague ? newLeague['teams'] : league['teams'])
            .filter(t => t.name === newSquadName)[0])['squad'] || [];
        const newSquadPlayers = newSquad.map(playerID => players.filter(p => p.id === playerID)[0])
        setSelectedSquad(newSquadPlayers);
    };

    const classes = useStyles();
    return (
        <Container>
            {!error ? '' :
                <Alert severity="error">
                    <AlertTitle>Server Error</AlertTitle>
                    {error}
                </Alert>
            }
            <div style={{justifyContent: 'center', marginBottom: '50px'}}>
                <Select labelId="choose squad" id='squadName' value={selectedSquadName}
                    onChange={(e) => onSquadChange(e)} className={classes.dropDown}>
                    {league.teams.map((squad, i) =>
                        <MenuItem selected={squad.name === selectedSquadName} key={squad.name} value={squad.name}>
                            {squad.name}
                        </MenuItem>
                    )}
                </Select>
            </div>
            <Accordion disabled={selectedSquadName === undefined} style={{marginBottom: '20px'}}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                    <Typography className={classes.heading}>Add players</Typography>
                </AccordionSummary>
                <AccordionDetails style={{flexWrap: 'wrap'}}>
                    <div style={{flexBasis: '100%'}}>
                    <TextField required className={classes.playerList} name="players" id='players' label="Player list"
                        multiline={true} minRows={11} disabled={selectedSquadName === undefined} />
                    </div>
                    <div style={{flexBasis: '100%'}}>
                    {displayListOfPlayers('Invalid players', inValidatedPlayers)}
                    </div>
                    <div style={{flexBasis: '100%'}}>
                        <Button className={classes.button} variant="contained" color="secondary"
                            onClick={validatePlayerName}>Validate</Button>
                        <Button className={classes.button} variant="contained" disabled={!addSquadEnabled}
                            onClick={addSquad}>Add Squad</Button>
                        {loading && <CircularProgress />}
                    </div>
                </AccordionDetails>
            </Accordion>
            <Accordion disabled={selectedSquadName === undefined}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                    <Typography className={classes.heading}>Current Squad</Typography>
                </AccordionSummary>
                <AccordionDetails style={{flexWrap: 'wrap'}}>
                    <div style={{flexBasis: '100%'}}>
                        <Button className={classes.button} variant="contained" disabled={removePlayersList.length === 0}
                            onClick={removeFromSquadHandler}>Remove Players</Button>
                        {loading && <CircularProgress />}
                    </div>
                    <div style={{flexBasis: '100%'}}>
                    {displayCurrentSquad(selectedSquad)}
                    </div>
                </AccordionDetails>
            </Accordion>
        </Container>
    )
};